import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { v4 as uuid } from "uuid";
import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt } from "@fortawesome/free-regular-svg-icons";
import { DoraButton, DoraSelect, DoraSelectOption } from "dora-shared/frontend";
import CreateShipmentData, {
  CreateEditShipmentStop,
} from "../CreateEditShipmentData";

const stopLabel = (stop: CreateEditShipmentStop, index: number) => {
  const place = stop.place?.placeName || stop.place?.city;
  const date = stop.date?.isValid ? stop.date.toFormat("dd/MM/yyyy") : "";
  return [place, date].filter(Boolean).join(" - ") || `#${index + 1}`;
};

const CmrConfigsDialog = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation(["shipmentModal", "components"]);
  const { control } = useFormContext<CreateShipmentData>();
  const pickupList = useWatch({ control, name: "pickupList" });
  const dropoffList = useWatch({ control, name: "dropoffList" });
  const { fields, append, remove } = useFieldArray({
    control,
    name: "cmrConfigs",
  });

  const pickupOptions: DoraSelectOption[] = pickupList.map((p, i) => ({
    value: (p.id || p.provisoryId)!,
    label: stopLabel(p, i),
  }));
  const dropoffOptions: DoraSelectOption[] = [
    {
      value: "",
      label: t("shipmentModal:cards.documents.cmrConfig.noDropoff"),
    },
    ...dropoffList.map((d, i) => ({
      value: (d.id || d.provisoryId)!,
      label: stopLabel(d, i),
    })),
  ];

  const isDuplicateAmong = (
    existing: { pickupId: string; dropoffId: string | null }[],
    pickupId: string,
    dropoffId: string | null
  ) =>
    existing.some(
      (f) => f.pickupId === pickupId && (f.dropoffId || null) === dropoffId
    );

  const isDuplicate = (pickupId: string, dropoffId: string | null) =>
    isDuplicateAmong(fields, pickupId, dropoffId);

  // First pickup/dropoff pair that isn't already configured - prefers a real
  // dropoff over "no dropoff" so a fresh dialog opens ready to add the next
  // pairing rather than the least useful one.
  const findNextAvailableCombo = (
    existing: { pickupId: string; dropoffId: string | null }[]
  ): { pickupId: string | null; dropoffId: string | null } => {
    const pickupIds = pickupList.map((p) => (p.id || p.provisoryId)!);
    const dropoffIds = dropoffList.map((d) => (d.id || d.provisoryId)!);
    for (const pickupId of pickupIds) {
      for (const dropoffId of dropoffIds) {
        if (!isDuplicateAmong(existing, pickupId, dropoffId)) {
          return { pickupId, dropoffId };
        }
      }
    }
    for (const pickupId of pickupIds) {
      if (!isDuplicateAmong(existing, pickupId, null)) {
        return { pickupId, dropoffId: null };
      }
    }
    return { pickupId: null, dropoffId: null };
  };

  const [newPickupId, setNewPickupId] = useState<string | null>(
    () => findNextAvailableCombo(fields).pickupId
  );
  const [newDropoffId, setNewDropoffId] = useState<string | null>(
    () => findNextAvailableCombo(fields).dropoffId
  );

  const currentIsDuplicate =
    !!newPickupId && isDuplicate(newPickupId, newDropoffId || null);
  const canAdd = !!newPickupId && !currentIsDuplicate;

  const onAdd = () => {
    if (!newPickupId || !canAdd) {
      return;
    }
    const dropoffId = newDropoffId || null;
    // A UUID: the server keeps it, so the config keeps its id across saves
    // and an open dialog recognises it when merging others' changes
    // (DORA-441).
    append({ id: uuid(), pickupId: newPickupId, dropoffId });
    const next = findNextAvailableCombo([
      ...fields,
      { pickupId: newPickupId, dropoffId },
    ]);
    setNewPickupId(next.pickupId);
    setNewDropoffId(next.dropoffId);
  };

  const labelForStopId = (options: DoraSelectOption[], stopId: string | null) =>
    options.find((o) => o.value === stopId)?.label;

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {t("shipmentModal:cards.documents.cmrConfig.title")}
      </DialogTitle>
      <DialogContent>
        {fields.length === 0 && (
          <div className="documents-card__cmr-config-empty">
            {t("shipmentModal:cards.documents.cmrConfig.empty")}
          </div>
        )}
        {fields.map((field, index) => (
          <div key={field.id} className="documents-card__cmr-config-row">
            <span>
              {labelForStopId(pickupOptions, field.pickupId)}
              {" → "}
              {field.dropoffId
                ? labelForStopId(dropoffOptions, field.dropoffId)
                : t("shipmentModal:cards.documents.cmrConfig.noDropoff")}
            </span>
            <IconButton size="small" onClick={() => remove(index)}>
              <FontAwesomeIcon icon={faTrashAlt} />
            </IconButton>
          </div>
        ))}
        <div className="documents-card__cmr-config-add-row">
          <DoraSelect
            label={t("shipmentModal:cards.documents.cmrConfig.pickupLabel")}
            options={pickupOptions}
            value={newPickupId || ""}
            fullWidth
            onChange={(v) => setNewPickupId(v || null)}
          />
          <DoraSelect
            label={t("shipmentModal:cards.documents.cmrConfig.dropoffLabel")}
            options={dropoffOptions}
            value={newDropoffId || ""}
            fullWidth
            onChange={(v) => setNewDropoffId(v || null)}
          />
          <DoraButton
            variant="primary-filled"
            disabled={!canAdd}
            onClick={onAdd}
          >
            {t("shipmentModal:cards.documents.cmrConfig.add")}
          </DoraButton>
          {currentIsDuplicate && (
            <div className="documents-card__cmr-config-duplicate-warning">
              {t("shipmentModal:cards.documents.cmrConfig.duplicateWarning")}
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <DoraButton variant="primary" onClick={onClose}>
          {t("components:buttonLabels.close")}
        </DoraButton>
      </DialogActions>
    </Dialog>
  );
};

export default CmrConfigsDialog;
